/**
 * 連絡帳やお知らせ一斉配信の本文にYouTubeリンクを貼った場合に
 * YouTubeリンクからサムネイルを生成する。
 *
 * 同時に、タグを除去したリンクテキストが youtube.com / m.youtube.com で始まる <a> タグを対象に、
 * XSS 対策として URL を HTML エスケープした上で iframe / 安全な <a> に再構築する。
 * （href 属性ではなく、<a> の内側テキストで YouTube リンクかを判定する点に注意）
 * これは Summernote のリンク挿入経由で <a> 内に raw な <script> 等の DOM 要素が
 * 混入するケース (Issue #19791) を塞ぐためのもの。
 *
 * @param {string} content
 * @returns {string} content YouTubeリンクを変換した後の本文
 */
export const generateYoutubeThumbnail = (content: string) => {
  // aタグを抽出
  const aTagPattern = new RegExp('<a(?: .+?)?>.*?</a>', 'g');
  const aTagElement = content.match(aTagPattern);

  if (!aTagElement) return content;

  // 抽出されたaタグからHTMLタグのみを除去する
  const htmlTagPattern = new RegExp('<("[^"]*"|\'[^\']*\'|[^\'">])*>', 'g');
  const filteredYoutubeATags = filterYoutubeUrl(aTagElement, htmlTagPattern);
  if (filteredYoutubeATags.length === 0) return content;

  return replaceFilteredATags(filteredYoutubeATags, content, htmlTagPattern);
};

const filterYoutubeUrl = (aTagElement: RegExpMatchArray, htmlTagPattern: RegExp) => {
  return aTagElement.filter((value) => {
    // youtubelinkのみ対象とする
    const linkUrl = value.replace(htmlTagPattern, '');
    return linkUrl.indexOf('https://www.youtube.com/') === 0 || linkUrl.indexOf('https://m.youtube.com/') === 0;
  });
};

const getVideoId = (linkUrl: string) => {
  if (linkUrl.indexOf('watch') === -1) return;

  // 上流の filterYoutubeUrl で youtube.com / m.youtube.com 始まりに限定済みかつ
  // ここでも watch を含むものだけを対象にするため、抽出経路は /watch?v= のみ。
  // YouTube の video_id は 11 文字固定。word boundary に依存すると、直後に word char
  // (例: <script> 等の混入で先頭が `a`) が続くケースで取りこぼすため {11} 固定で取得する。
  const match = linkUrl.match(/\/watch\?v=([\w\-]{11})/);
  if (!match) return;

  return match[1];
};

const escapeHtml = (str: string): string => {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

// <a>...</a> の内部 (タグ本体と </a> の間) に追加の HTML 要素 (<script> 等) が
// 混入しているかを判定。 Summernote 経由で raw な script element が text 子として
// 混入するケースを検知して安全な <a> に置き換えるトリガーに使う。
const hasInnerHtmlElements = (aTag: string): boolean => {
  const openEnd = aTag.indexOf('>');
  const closeStart = aTag.lastIndexOf('</a>');
  if (openEnd < 0 || closeStart < 0) return false;
  return /<[a-zA-Z]/.test(aTag.slice(openEnd + 1, closeStart));
};

const buildIframeReplacement = (videoId: string, escapedUrl: string) =>
  `<iframe src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe><br><a href="${escapedUrl}" style="color:#aaa;" target="_blank" rel="noopener noreferrer">URL: ${escapedUrl}</a>`;

const buildSafeAnchorReplacement = (escapedUrl: string) =>
  `<a href="${escapedUrl}" target="_blank" rel="noopener noreferrer">${escapedUrl}</a>`;

const replaceFilteredATags = (filteredYoutubeATags: string[], content: string, htmlTagPattern: RegExp) => {
  filteredYoutubeATags.forEach((aTag) => {
    const linkUrl = aTag.replace(htmlTagPattern, '');
    const videoId = getVideoId(linkUrl);
    if (videoId) {
      const escapedUrl = escapeHtml(linkUrl);
      content = content.replace(aTag, buildIframeReplacement(videoId, escapedUrl));
    } else if (hasInnerHtmlElements(aTag)) {
      // video_id が取れない youtube.com URL だが、 <a> の内部に追加の DOM 要素が混入。
      // raw script / svg / img などの XSS 経路が含まれている可能性が高いので、
      // テキスト化した URL のみを持つ安全な <a> に置き換える。
      const escapedUrl = escapeHtml(linkUrl);
      content = content.replace(aTag, buildSafeAnchorReplacement(escapedUrl));
    }
  });

  return content;
};
